<template>
  <div class="container-md col-lg-11 col-md-11 db-switch pb-4 mb-4">
    <h3 class="text-primary mb-1">Database switch</h3>
    <p class="text-muted">
      Switch the whole system between the full and the clean database.
    </p>

    <LoadingOverlay v-if="isFetching && !status" />

    <CAlert v-if="loadError" color="danger">{{ loadError }}</CAlert>

    <CAlert
      v-if="refusal"
      color="danger"
      closeButton
      :show="true"
      @update:show="refusal = null"
    >
      <strong>{{ refusal }}</strong> Nothing was switched.
    </CAlert>

    <template v-if="status">
      <CAlert
        :color="status.canSwitchNow ? 'success' : 'warning'"
        class="d-flex flex-wrap justify-content-between"
      >
        <strong
          >Active database: {{ status.activeDatabase }}
          <span v-if="activeLabel">({{ activeLabel }})</span></strong
        >
        <span>{{
          status.canSwitchNow ? "Switching available now" : status.blockedReason
        }}</span>
      </CAlert>

      <CRow>
        <CCol
          v-for="db in status.databases"
          :key="db.name"
          md="6"
          class="mb-3"
        >
          <CCard
            class="h-100 mb-0"
            :class="{
              'border-primary db-card-active': db.active,
              'db-card-unreachable': !db.reachable,
            }"
          >
            <CCardBody>
              <div class="d-flex justify-content-between align-items-start">
                <h5 class="mb-1">{{ db.name }}</h5>
                <CBadge v-if="db.active" color="primary">ACTIVE</CBadge>
              </div>
              <p class="text-muted mb-3">{{ db.label }}</p>

              <template v-if="db.reachable">
                <p class="mb-2">
                  {{ formatCount(db.candidates) }} candidates ·
                  {{ formatCount(db.jobs) }} jobs ·
                  {{ formatCount(db.users) }} users
                </p>
                <p class="small mb-3" :class="{ 'text-danger': !db.schemaMatchesActive }">
                  {{
                    db.schemaMatchesActive
                      ? "Same version as active ✓"
                      : "Not at the same version - can't switch"
                  }}
                </p>
                <p v-if="db.active" class="small text-muted mb-0">
                  The system is using this database
                </p>
                <div v-else class="d-flex align-items-center flex-wrap">
                  <CButton
                    color="danger"
                    variant="outline"
                    class="mr-2"
                    :disabled="!status.canSwitchNow || !db.schemaMatchesActive"
                    @click="confirmTarget = db"
                    >Switch to {{ db.name }}</CButton
                  >
                  <span v-if="!status.canSwitchNow" class="small text-muted">{{
                    status.blockedReason
                  }}</span>
                </div>
              </template>
              <p v-else class="text-danger mb-0">{{ db.error }}</p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <h5 class="mt-2">Switch history (last 20)</h5>
      <CDataTable
        :items="status.history"
        :fields="historyFields"
        small
        class="border px-1"
        :noItemsView="{ noResults: 'No switches yet', noItems: 'No switches yet' }"
      >
        <template #startedOn="{ item }">
          <td>{{ formatTime(item.startedOn) }}</td>
        </template>
        <template #fromTo="{ item }">
          <td>{{ item.fromDatabase }} → {{ item.toDatabase }}</td>
        </template>
        <template #result="{ item }">
          <td>
            <CBadge :color="resultColor(item.result)">{{ item.result }}</CBadge>
            <div
              v-if="item.detail && ['aborted', 'failed'].includes(item.result)"
              class="small text-muted mt-1"
            >
              {{ item.detail }}
            </div>
          </td>
        </template>
      </CDataTable>
    </template>

    <CCard class="mt-3">
      <CCardHeader
        class="cursor-pointer"
        role="button"
        @click="showAbout = !showAbout"
      >
        <em
          class="fas mr-2"
          :class="showAbout ? 'fa-chevron-down' : 'fa-chevron-right'"
        ></em>
        About the database switch
      </CCardHeader>
      <CCollapse :show="showAbout">
        <CCardBody>
          <ul class="mb-0">
            <li>
              <strong>What it does.</strong> The whole TalentFind system on this
              environment - the candidate portal, recruiter and hospital screens,
              the admin screens and the overnight jobs - starts using the database
              you pick.
            </li>
            <li>
              <strong>The clean database</strong> holds only reference data
              (specialties, qualifications, countries, PQR rules, lookups and
              settings), the TalentFind Marketplace (TFM) and this admin account.
              It has no candidates, jobs, applications, documents, messages, other
              customers or training courses. Only the TFM portal and the admin
              login work there.
            </li>
            <li>
              <strong>Nothing is copied.</strong> The two databases are separate:
              changes in one never appear in the other, and switching back shows
              the other one exactly as it was left.
            </li>
            <li>
              <strong>Everyone is logged out</strong> at every switch, including
              you. For 15-45 seconds nobody can use the system.
            </li>
            <li>
              <strong>Not during the night.</strong> Switching is blocked between
              23:45 and 04:30 UTC (05:15-10:00 IST) while the overnight jobs run.
            </li>
            <li>
              <strong>Both databases must be at the same version.</strong> If not,
              the switch is refused and nothing changes.
            </li>
            <li>
              <strong>If anything goes wrong, nothing is switched.</strong> The
              system stays on the current database, and the history shows what
              happened.
            </li>
            <li>
              <strong>Not affected:</strong> Moodle (learning), Superset dashboards
              and the database MCP keep using the full database.
            </li>
            <li>
              <strong>This environment only.</strong> QA and staging switch
              separately; production has no switch.
            </li>
          </ul>
        </CCardBody>
      </CCollapse>
    </CCard>

    <DbSwitchConfirmModal
      v-if="confirmTarget"
      :database="confirmTarget"
      @cancel="confirmTarget = null"
      @confirm="startSwitch(confirmTarget)"
    />

    <DbSwitchProgress
      v-if="progress"
      :target="progress.target"
      :phase="progress.phase"
      :message="progress.message"
      :activeDatabase="progress.activeDatabase"
      :lastSwitch="progress.lastSwitch"
      @login-again="dbSwitchedLogout"
      @close="closeProgress"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import DbSwitchConfirmModal from "./DbSwitchConfirmModal.vue";
import DbSwitchProgress from "./DbSwitchProgress.vue";

// No answer to the switch POST (network error / timeout): wait this long, then look at the status
// instead of retrying - the switch may well have happened.
const CHECK_AFTER_NO_ANSWER_MS = 10000;

// What to show when the backend refuses a switch, by its error code.
const REFUSALS = {
  CONFIRMATION_REQUIRED: () => "Type the database name exactly to confirm.",
  ALREADY_ACTIVE: (db) => `${db} is already the active database.`,
  NIGHTLY_JOBS_WINDOW: () =>
    "Nightly jobs are running. Switch between 04:30 and 23:45 UTC (10:00-05:15 IST).",
  SCHEMA_MISMATCH: (db) =>
    `The two databases aren't at the same version. Ask the backend team to update ${db} first.`,
  ADMIN_MISSING_IN_TARGET: (db) =>
    `You don't exist as a system admin in ${db}, so nobody could switch back.`,
  SWITCH_IN_PROGRESS: () =>
    "Another switch is in progress - wait a minute and refresh.",
  STILL_BUSY: () =>
    "The system was busy and nothing was switched. Try again in a few minutes.",
  NOT_ALLOWED: () => "Only named system admins can switch the database.",
};

const RESULT_COLORS = {
  done: "success",
  aborted: "warning",
  failed: "danger",
  started: "info",
};

const errorOf = (err) => {
  const { status, data } = err?.response || {};
  const detail = data?.detail;
  return {
    status,
    code: detail?.code,
    message: (typeof detail === "string" ? detail : detail?.message) || "",
  };
};

export default {
  name: "DbSwitch",
  components: { DbSwitchConfirmModal, DbSwitchProgress },
  data() {
    return {
      isFetching: false,
      loadError: "",
      refusal: null,
      confirmTarget: null,
      // { target, phase, message, activeDatabase, lastSwitch } while the switch dialog is up.
      progress: null,
      showAbout: false,
      historyFields: [
        { key: "startedOn", label: "When" },
        { key: "byEmail", label: "By" },
        { key: "fromTo", label: "From → To" },
        { key: "result", label: "Result" },
      ],
    };
  },
  computed: {
    ...mapGetters({ status: "getDbSwitchStatus" }),
    activeLabel() {
      const active = (this.status?.databases || []).find((db) => db.active);
      return active?.label || "";
    },
    isSwitchRunning() {
      return ["running", "checking"].includes(this.progress?.phase);
    },
  },
  methods: {
    ...mapActions([
      "fetchDbSwitchStatus",
      "switchDatabase",
      "setOwnDbSwitchRunning",
      "dbSwitchedLogout",
    ]),
    formatCount(n) {
      return (n || 0).toLocaleString();
    },
    formatTime(iso) {
      return iso ? moment.utc(iso).format("DD MMM HH:mm [UTC]") : "";
    },
    resultColor(result) {
      return RESULT_COLORS[result] || "secondary";
    },
    loadStatus() {
      this.isFetching = true;
      this.loadError = "";
      return this.fetchDbSwitchStatus()
        .catch((err) => {
          const { code, message } = errorOf(err);
          this.loadError =
            code === "NOT_ALLOWED"
              ? REFUSALS.NOT_ALLOWED()
              : message || "Couldn't load the database switch status.";
        })
        .finally(() => {
          this.isFetching = false;
        });
    },
    async startSwitch(db) {
      this.confirmTarget = null;
      this.refusal = null;
      // From here every other call this admin makes gets 503 MAINTENANCE and then 401
      // DB_SWITCHED - this dialog reports them instead of the overlay / automatic logout.
      this.setOwnDbSwitchRunning(true);
      this.progress = { target: db.name, phase: "running" };
      try {
        const res = await this.switchDatabase(db.name);
        this.showSwitched(res?.data?.message);
      } catch (err) {
        const { status, code, message } = errorOf(err);
        if (!status) {
          this.progress = { ...this.progress, phase: "checking" };
          setTimeout(this.checkAfterNoAnswer, CHECK_AFTER_NO_ANSWER_MS);
        } else if (status === 401 && code === "DB_SWITCHED") {
          // Someone else switched before this request: this login is over anyway.
          this.dbSwitchedLogout();
        } else {
          this.progress = null;
          this.setOwnDbSwitchRunning(false);
          const refusal = REFUSALS[code];
          this.refusal = refusal ? refusal(db.name) : message || "The switch failed.";
          this.loadStatus();
        }
      }
    },
    showSwitched(message) {
      const target = this.progress.target;
      this.progress = {
        target,
        phase: "success",
        message:
          message ||
          `Switched to ${target}. Everyone has been logged out, including you.`,
      };
    },
    // Never retry the switch itself: ask what happened instead.
    checkAfterNoAnswer() {
      this.fetchDbSwitchStatus()
        .then(({ data }) => {
          this.setOwnDbSwitchRunning(false);
          this.progress = {
            ...this.progress,
            phase: "unknown",
            message: "The server didn't answer in time. This is where things stand now:",
            activeDatabase: data.activeDatabase,
            lastSwitch: (data.history || [])[0] || null,
          };
        })
        .catch((err) => {
          const { status, code } = errorOf(err);
          // Our token died with the switch: it did happen.
          if (status === 401 && code === "DB_SWITCHED") {
            this.showSwitched();
            return;
          }
          this.setOwnDbSwitchRunning(false);
          this.progress = {
            ...this.progress,
            phase: "unknown",
            message:
              "The server didn't answer in time and the status couldn't be loaded. Refresh the page in a minute to see whether the switch happened.",
          };
        });
    },
    closeProgress() {
      this.progress = null;
    },
    warnBeforeUnload(event) {
      if (!this.isSwitchRunning) return;
      event.preventDefault();
      event.returnValue = "";
    },
  },
  created() {
    this.loadStatus();
  },
  mounted() {
    window.addEventListener("beforeunload", this.warnBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.warnBeforeUnload);
    // After a switch this login is dead: leaving the page means logging in again.
    if (this.progress?.phase === "success") {
      this.dbSwitchedLogout();
    } else {
      this.setOwnDbSwitchRunning(false);
    }
  },
  beforeRouteLeave(to, from, next) {
    next(!this.isSwitchRunning);
  },
};
</script>

<style scoped>
.db-card-active {
  border-width: 2px;
}
.db-card-unreachable {
  opacity: 0.6;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
